import { AxiosResponse } from 'axios-cad-nx';
import {
    ApiResultUnitLocationInfoView,
    UnitLocationBootstrapResponse,
    UnitLocationResource,
    UnitLocationUpdateRequest,
} from '@mark43/cad-api';

import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { UnitLocationFormType } from './unit-location-form';

export interface UseUnitLocationFormReturnType {
    query: ReturnType<typeof useQuery>;
    mutation: UseMutationResult<
        AxiosResponse<ApiResultUnitLocationInfoView, any>,
        unknown,
        UnitLocationUpdateRequest,
        unknown
    >;
    formData: UnitLocationBootstrapResponse | undefined;
    submitForm: (data: UnitLocationFormType) => void;
}

export function useUnitLocationForm(
    unitId: number,
    closeDrawer: (...args: any[]) => any
): UseUnitLocationFormReturnType {
    const apiClient = useApiClient();

    const query = useQuery(['unit-location-view', unitId], () => {
        return apiClient
            .request<UnitLocationResource.BootstrapUnitLocation>({
                method: 'POST',
                url: 'unit_location/bootstrap',
                data: { unitId },
            })
            .then((resp) => resp.data?.data);
    });

    const { data: unitLocationView } = query;

    const mutation = useMutation((unitLocationForm: UnitLocationUpdateRequest) => {
        return apiClient.request<UnitLocationResource.UpdateUnitLocation>({
            method: 'POST',
            url: 'unit_location/update',
            data: unitLocationForm,
        });
    });

    const { mutate } = mutation;

    /**
     * In order for form submission to work, the location must have exactly one entity link.
     * Here, if we're trying to submit a location we ensure we use the first entity link.
     */
    const submitForm = async (form: UnitLocationFormType): Promise<void> => {
        let finalForm = form;
        const selectedLocation = form.consolidatedLocationView;
        const selectedEntityLink = selectedLocation?.entityLinks?.[0];

        if (selectedLocation !== undefined && selectedEntityLink !== undefined) {
            finalForm = {
                ...form,
                consolidatedLocationView: {
                    ...selectedLocation,
                    entityLinks: [selectedEntityLink],
                },
            };
        }

        return mutate(finalForm, {
            onSuccess: closeDrawer,
        });
    };

    return { formData: unitLocationView, submitForm, query, mutation };
}
